import React, { useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import csrfToken from '../../shared/adminCsrf';
import SPAButton from '../../shared/SPAButton';
import SPAPopover from '../../shared/SPAPopover';
import SPATooltip from '../../shared/SPATooltip';
import { LessonIcon, ProgramLesson } from './types';
import { useLessonSavedFlash } from './lessonSavedFlash';
import { useIcons, usePatchLesson } from './useProgramLessons';

interface NumberCellProps {
  lesson: ProgramLesson;
  programId: number;
}

interface EditorProps extends NumberCellProps {
  onSaved: () => void;
}

// All three display modes share the input's footprint so the column doesn't shift
// as rows switch between number, icon, and none.
const displaySlotClass = cn(
  'tw-w-14 tw-h-8 tw-box-border tw-rounded tw-border tw-border-solid tw-border-spa-border-input',
  'tw-bg-spa-bg tw-text-sm tw-text-spa-primary',
);

const inputClass = cn(
  displaySlotClass,
  'tw-px-1.5 focus:tw-outline focus:tw-outline-2 focus:tw-outline-spa-accent',
);

const staticSlotClass = cn(displaySlotClass, 'tw-inline-flex tw-items-center tw-justify-center');

const caretClass = 'tw-text-xs tw-text-spa-tertiary hover:tw-text-spa-accent-text tw-transition-colors';
// The standalone caret is a target of its own, so it is a square the height of the display box
// (never under 24px, WCAG 2.5.8) with the glyph centred, rather than a glyph with a sliver of padding.
const caretButtonClass = cn(
  'tw-flex tw-h-[max(2rem,24px)] tw-w-[max(1.5rem,24px)] tw-items-center tw-justify-center tw-rounded',
  'tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-bg-spa-bg-tertiary',
  caretClass,
);

// The engineering icon's legacy class is a fixed 26px block with an unsized background,
// so it dwarfs the ~14px font glyphs and sits off-center — scale it to match them.
const glyphClass = (iconClass: string) => cn(
  iconClass,
  iconClass === 'tci_eng_design_icon' && 'tw-h-6 tw-w-6 tw-bg-contain tw-bg-center',
);

const iconButtonClass = (active: boolean) => cn(
  'tw-flex tw-items-center tw-justify-center tw-h-10 tw-w-10 tw-box-border tw-rounded',
  'tw-border tw-border-solid tw-bg-transparent tw-cursor-pointer tw-transition-colors',
  // Font glyphs (fa, stateface) scale with font-size; the eng icon is sized in glyphClass.
  'tw-text-spa-primary tw-text-lg',
  active
    ? 'tw-border-spa-accent tw-bg-spa-accent-bg-subtle'
    : 'tw-border-spa-border hover:tw-border-spa-accent',
);

// Inline number editor — the legacy fast path, minus the jQuery auto-save plumbing.
const NumberInput = ({ lesson, programId, onSaved }: EditorProps) => {
  const [value, setValue] = useState(lesson.title_number ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const patchLesson = usePatchLesson(programId);

  const save = () => {
    if (value === (lesson.title_number ?? '')) return;

    setIsSaving(true);
    Axios
      .put(
        Routes.set_title_number_admin_program_lessons_path(programId, { format: 'js' }),
        { program_lesson_id: lesson.program_lesson_id, title_number: value },
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .then(() => {
        patchLesson(lesson.id, { title_number: value });
        onSaved();
      })
      .catch(() => {})
      .finally(() => setIsSaving(false));
  };

  return (
    <input
      aria-label={`Number for ${lesson.title}`}
      className={inputClass}
      disabled={isSaving}
      min={1}
      type="number"
      value={value}
      onBlur={save}
      onChange={e => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
};

const DisplayTrigger = ({ lesson }: { lesson: ProgramLesson }) => (
  <SPAPopover.Trigger asChild>
    {lesson.display_type === 'number' ? (
      <button
        aria-label={`Edit number display for ${lesson.title}`}
        className={caretButtonClass}
        type="button"
      >
        ▾
      </button>
    ) : (
      // In icon/none modes the display box itself is part of the trigger, so clicking
      // it (not just the caret) opens the editor.
      <button
        aria-label={`Edit number display for ${lesson.title}`}
        className="tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
        type="button"
      >
        <span
          aria-hidden="true"
          className={cn(
            staticSlotClass,
            'hover:tw-border-spa-accent tw-transition-colors',
            lesson.display_type === 'none' && 'tw-text-spa-tertiary',
          )}
        >
          {lesson.display_type === 'icon'
            ? lesson.icon_class && <span aria-hidden="true" className={glyphClass(lesson.icon_class)} />
            : '—'}
        </span>
        <span aria-hidden="true" className={caretClass}>▾</span>
      </button>
    )}
  </SPAPopover.Trigger>
);

const DisplayContent = ({ lesson, programId, onSaved }: EditorProps) => {
  const { data: icons = [] } = useIcons();
  const [pickingIcon, setPickingIcon] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const patchLesson = usePatchLesson(programId);

  const saveDisplayType = (displayType: string, icon?: LessonIcon) => {
    setIsSaving(true);
    setError(false);
    Axios
      .put(
        Routes.set_display_type_admin_lesson_path(lesson.id, { format: 'json', program_id: programId }),
        { display_type: displayType, ...(icon && { icon_id: icon.id }) },
        { headers: { 'X-CSRF-Token': csrfToken() } },
      )
      .then(() => {
        patchLesson(lesson.id, {
          display_type: displayType,
          ...(icon && { icon_class: icon.icon_class, icon_id: icon.id }),
        });
        setPickingIcon(false);
        onSaved();
      })
      .catch(() => setError(true))
      .finally(() => setIsSaving(false));
  };

  const showIcons = pickingIcon || lesson.display_type === 'icon';

  const segmentVariant = (active: boolean) => (active ? 'primary' as const : 'secondary' as const);

  return (
    <SPAPopover.Content align="start" className="tw-p-4">
      <div className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-heading tw-mb-2.5">
        Display
      </div>
      <div className="tw-flex tw-gap-1.5">
        <SPAButton
          disabled={isSaving}
          variant={segmentVariant(lesson.display_type === 'number' && !pickingIcon)}
          onClick={() => saveDisplayType('number')}
        >
          #
        </SPAButton>
        <SPAButton
          disabled={isSaving}
          variant={segmentVariant(showIcons)}
          onClick={() => {
            if (lesson.icon_id) saveDisplayType('icon');
            else setPickingIcon(true);
          }}
        >
          Icon
        </SPAButton>
        <SPAButton
          disabled={isSaving}
          variant={segmentVariant(lesson.display_type === 'none' && !pickingIcon)}
          onClick={() => saveDisplayType('none')}
        >
          None
        </SPAButton>
      </div>

      {showIcons && (
        <div className="tw-mt-3 tw-flex tw-gap-1.5">
          {icons.map(icon => (
            <SPATooltip key={icon.id}>
              <SPATooltip.Trigger asChild>
                <button
                  aria-label={icon.name}
                  className={iconButtonClass(lesson.display_type === 'icon' && lesson.icon_id === icon.id)}
                  disabled={isSaving}
                  type="button"
                  onClick={() => saveDisplayType('icon', icon)}
                >
                  <span aria-hidden="true" className={glyphClass(icon.icon_class)} />
                </button>
              </SPATooltip.Trigger>
              <SPATooltip.Content>{icon.name}</SPATooltip.Content>
            </SPATooltip>
          ))}
        </div>
      )}

      {error && (
        <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-spa-error">
          Couldn’t save. Try again.
        </p>
      )}
    </SPAPopover.Content>
  );
};

const NumberCell = ({ lesson, programId }: NumberCellProps) => {
  const { flashLesson, flashedLessonId } = useLessonSavedFlash();

  // Opening/closing lessons never show a number or the editor (legacy parity).
  if (lesson.kind === 'opening' || lesson.kind === 'closing') return null;

  const flashSaved = () => flashLesson(lesson.id);

  return (
    <SPAPopover>
      {/* Anchor the popover to the whole cell group so it always opens from the
          bottom-left of the input/box, not wherever the trigger happens to sit. */}
      <SPAPopover.Anchor asChild>
        {/* tw-relative contains the absolute sr-only status span — without a positioned
            ancestor it escapes the main scroller's clipping and stretches the page. */}
        <div className="tw-relative tw-inline-flex tw-items-center tw-gap-1">
          {lesson.display_type === 'number' && (
            <NumberInput lesson={lesson} programId={programId} onSaved={flashSaved} />
          )}
          <DisplayTrigger lesson={lesson} />
          <span className="tw-sr-only" role="status">
            {flashedLessonId === lesson.id ? 'Saved' : ''}
          </span>
        </div>
      </SPAPopover.Anchor>
      <DisplayContent lesson={lesson} programId={programId} onSaved={flashSaved} />
    </SPAPopover>
  );
};

export default NumberCell;
